import React, { useCallback, useEffect, useRef, useState } from "react";
import cx from "classnames";
import deviceInfo from "utils/deviceInfo";
import Icon from "../utils/icon";
import "./index.scss";

export const WrapperDragToScroll = ({
  children,
  className,
  arrow,
  containerClassName,
  scrollOffset = 300,
}) => {
  const ref = useRef();
  const isMobile = deviceInfo.isMobile;
  const isTablet = deviceInfo.isMobile;
  const [state, setState] = useState({
    isGrab: false,
    position: { top: 0, left: 0, x: 0, y: 0 },
    disabledPrev: true,
    disabledNext: false,
    hasHorizontalScroll: false,
  });
  const { isGrab, position, disabledNext, disabledPrev, hasHorizontalScroll } =
    state;
  const showNextPrevBtn = !isTablet && !isMobile && hasHorizontalScroll;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const checkScroll = () => {
      setState((prev) => ({
        ...prev,
        hasHorizontalScroll: el.scrollWidth > el.clientWidth,
      }));
    };

    const observer = new ResizeObserver(() => {
      checkScroll();
    });
    observer.observe(el);
    checkScroll();

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("mousemove", handleMouseMove);
    return () => {
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("mousemove", handleMouseMove);
    };
  }, [position, isGrab]);

  const handlePrev = useCallback(() => {
    if (ref?.current) {
      const { scrollLeft, offsetWidth, scrollWidth } = ref.current;
      if (offsetWidth < scrollWidth) {
        setState((prevState) => ({
          ...prevState,
          disabledPrev: false,
          disabledNext: false,
        }));
      }
      if (scrollLeft - scrollOffset <= 0 || offsetWidth === scrollWidth) {
        setState((prevState) => ({
          ...prevState,
          disabledPrev: true,
        }));
      }
      ref.current.scrollLeft -= scrollOffset;
    }
  }, [scrollOffset]);

  const handleNext = useCallback(() => {
    if (ref.current) {
      const { scrollLeft, offsetWidth, scrollWidth } = ref.current;
      if (offsetWidth < scrollWidth) {
        setState((prevState) => ({
          ...prevState,
          disabledPrev: false,
          disabledNext: false,
        }));
      }
      if (scrollLeft + scrollOffset + offsetWidth >= scrollWidth) {
        setState((prevState) => ({
          ...prevState,
          disabledNext: true,
        }));
      }
      ref.current.scrollLeft += scrollOffset;
    }
  }, [scrollOffset]);

  const handleMouseMove = useCallback(
    (e) => {
      if (isGrab) {
        const dx = e.clientX - position.x;
        const dy = e.clientY - position.y;
        ref.current.scrollTop = position.top - dy;
        ref.current.scrollLeft = position.left - dx;
      }
    },
    [position, isGrab]
  );

  const handleMouseUp = useCallback(() => {
    ref.current.style.cursor = "grab";
    ref.current.style.removeProperty("user-select");
    ref.current.style.scrollBehavior = "smooth";
    setState((prevState) => ({
      ...prevState,
      isGrab: false,
    }));
    // if (typeof onDragEnd === 'function') {
    //   const { scrollLeft, offsetWidth, scrollWidth } = ref.current
    //   const disabledNext = scrollLeft + offsetWidth >= scrollWidth
    //   const disabledPrev = scrollLeft <= 0
    //   onDragEnd(disabledPrev, disabledNext)
    // }
  }, []);

  const handleMouseDown = useCallback((e) => {
    if (arrow) return;
    ref.current.style.cursor = "grabbing";
    ref.current.style.userSelect = "none";
    ref.current.style.scrollBehavior = "unset";
    const newPosition = {
      // The current scroll
      left: ref.current.scrollLeft,
      top: ref.current.scrollTop,
      // Get the current mouse position
      x: e.clientX,
      y: e.clientY,
    };
    setState((prevState) => ({
      ...prevState,
      isGrab: true,
      position: newPosition,
    }));
  }, []);

  const renderContent = () => (
    <div
      ref={ref}
      className={cx({
        "wrapper-drag-to-scroll__content": true,
        [className]: !!className,
      })}
      onMouseDown={handleMouseDown}
    >
      {children}
    </div>
  );

  if (!arrow) return renderContent();

  return (
    <div
      className={cx({
        "wrapper-drag-to-scroll": true,
        [containerClassName]: !!containerClassName,
      })}
    >
      {showNextPrevBtn && (
        <div
          style={{
            opacity: disabledPrev ? 0.2 : 1,
          }}
          className="wrapper-drag-to-scroll__icon-left"
        >
          <Icon name="left" onClick={handlePrev} size={24} />
        </div>
      )}
      {renderContent()}
      {showNextPrevBtn && (
        <div
          className="wrapper-drag-to-scroll__icon-right"
          style={{
            opacity: disabledNext ? 0.5 : 1,
          }}
        >
          <Icon name="right" onClick={handleNext} size={24} />
        </div>
      )}
    </div>
  );
};
